import { loadRemote } from "@module-federation/enhanced/runtime";
import log from "../utils/logger/library";
import { isServer } from "../utils/environmentDetection/library";
import { ACC_CONFIG } from "./accountCentre";

/**
 * Dynamically import module federation remote modules client side.
 *
 * @param {string} scopeName The scope of the requested remote module (E.g. "membership").
 * @param {string} moduleName The name of the requested remote module defined in the remote's shared modules (E.g. "./actions").
 *
 * @returns {Promise} Requested remote module (object or function)
 */
const importMF = async ({
    moduleName,
    exportName = "",
    scope = ACC_CONFIG.scope,
}) => {
    const formattedModuleName = moduleName.replace("./", "");
    const moduleId = `${scope}/${formattedModuleName}`;

    try {
        if (isServer()) {
            throw new Error(`Module "${moduleId}" is not available serverside`);
        }

        // Fetch remote module
        const remoteModule = await loadRemote(moduleId);

        if (!remoteModule || remoteModule?.error?.message) {
            throw new Error("Failed to fetch remote module.");
        }

        if (exportName) {
            const exportedModule = remoteModule[exportName];

            if (!exportedModule) {
                throw new Error(
                    `Failed to find export "${exportName}" in module "${moduleName}".`
                );
            }

            return exportedModule;
        } else {
            return remoteModule;
        }
    } catch (error) {
        const errorName = error && error.name ? error.name : "Error";
        const requestedModuleName = exportName
            ? `"${exportName}" from "${moduleId}"`
            : `"${moduleId}"`;

        log.error(
            `(importMF) Failed to import ${requestedModuleName}. [${errorName}: ${error.message}]`
        );

        throw new Error(
            `${errorName}: Failed to import ${requestedModuleName}`,
            { cause: error }
        );
    }
};

export default importMF;
